import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Box, Drawer, IconButton, Typography, Stack, Chip, Button, Divider, Tooltip, Paper, Snackbar, Alert,
} from '@mui/material';
import { HelpOutline, Close, CheckCircle, Lightbulb, WarningAmber, AutoAwesome, ArrowForward } from '@mui/icons-material';
import { ayudaDeRuta, AyudaModulo as Ayuda } from '../content/ayudaModulos';

/** Abre el asistente de ayuda (AyudaChatbot) con una pregunta precargada. */
export function preguntarAlAsistente(pregunta: string) {
  window.dispatchEvent(new CustomEvent('respondo:preguntar-ayuda', { detail: pregunta }));
}

/** Abre el panel de ayuda del módulo actual desde cualquier parte. */
export function abrirAyudaModulo() {
  window.dispatchEvent(new CustomEvent('respondo:abrir-ayuda-modulo'));
}

const Seccion: React.FC<{ titulo: string; children: React.ReactNode }> = ({ titulo, children }) => (
  <Box sx={{ mb: 2.5 }}>
    <Typography variant="overline" sx={{ color: 'primary.dark', fontWeight: 700, letterSpacing: 1 }}>{titulo}</Typography>
    {children}
  </Box>
);

const PanelAyuda: React.FC<{ ayuda: Ayuda; onClose: () => void }> = ({ ayuda, onClose }) => {
  const navigate = useNavigate();
  return (
    <Box sx={{ width: { xs: '100vw', sm: 440 }, display: 'flex', flexDirection: 'column', height: '100%' }}>
      <Box sx={{ background: 'linear-gradient(135deg,#075E54,#25D366)', color: '#fff', p: 2.5 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
          <Box>
            <Typography variant="caption" sx={{ opacity: 0.85 }}>Cómo funciona</Typography>
            <Typography variant="h5" sx={{ fontWeight: 800 }}>{ayuda.emoji} {ayuda.titulo}</Typography>
          </Box>
          <IconButton onClick={onClose} sx={{ color: '#fff' }} aria-label="Cerrar ayuda"><Close /></IconButton>
        </Stack>
        <Typography variant="body2" sx={{ mt: 1, opacity: 0.95, lineHeight: 1.5 }}>{ayuda.resumen}</Typography>
      </Box>

      <Box sx={{ p: 2.5, overflowY: 'auto', flexGrow: 1 }}>
        {ayuda.paraQue.length > 0 && (
          <Seccion titulo="Para qué sirve">
            <Stack spacing={0.8} sx={{ mt: 0.5 }}>
              {ayuda.paraQue.map((p) => (
                <Stack key={p} direction="row" spacing={1} alignItems="flex-start">
                  <CheckCircle sx={{ fontSize: 18, color: '#25D366', mt: '2px' }} />
                  <Typography variant="body2">{p}</Typography>
                </Stack>
              ))}
            </Stack>
          </Seccion>
        )}

        {ayuda.pasos.length > 0 && (
          <Seccion titulo="Paso a paso">
            <Stack spacing={1.2} sx={{ mt: 0.5 }}>
              {ayuda.pasos.map((p, i) => (
                <Stack key={i} direction="row" spacing={1.5} alignItems="flex-start">
                  <Box sx={{ minWidth: 26, height: 26, borderRadius: '50%', bgcolor: '#075E54', color: '#fff', fontSize: 13, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{i + 1}</Box>
                  <Typography variant="body2" sx={{ pt: '3px' }}>{p}</Typography>
                </Stack>
              ))}
            </Stack>
          </Seccion>
        )}

        {ayuda.ejemplos.length > 0 && (
          <Seccion titulo="Ejemplos">
            <Stack spacing={1.5} sx={{ mt: 0.5 }}>
              {ayuda.ejemplos.map((e, i) => (
                <Paper key={i} variant="outlined" sx={{ p: 1.5, bgcolor: e.cliente ? '#ECE5DD' : '#F8FAFC', borderRadius: 2 }}>
                  <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary' }}>{e.titulo}</Typography>
                  {e.cliente && (
                    <Box sx={{ display: 'flex', mt: 1 }}>
                      <Box sx={{ bgcolor: '#fff', p: 1, px: 1.5, borderRadius: 2, maxWidth: '85%' }}><Typography variant="body2">{e.cliente}</Typography></Box>
                    </Box>
                  )}
                  {e.agente && (
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
                      <Box sx={{ bgcolor: '#DCF8C6', p: 1, px: 1.5, borderRadius: 2, maxWidth: '85%' }}><Typography variant="body2">{e.agente}</Typography></Box>
                    </Box>
                  )}
                  {e.texto && <Typography variant="body2" sx={{ mt: 0.5, whiteSpace: 'pre-line' }}>{e.texto}</Typography>}
                </Paper>
              ))}
            </Stack>
          </Seccion>
        )}

        {ayuda.tips.length > 0 && (
          <Seccion titulo="Consejos">
            <Stack spacing={0.8} sx={{ mt: 0.5 }}>
              {ayuda.tips.map((t) => (
                <Stack key={t} direction="row" spacing={1} alignItems="flex-start">
                  <Lightbulb sx={{ fontSize: 18, color: '#F59E0B', mt: '2px' }} />
                  <Typography variant="body2">{t}</Typography>
                </Stack>
              ))}
            </Stack>
          </Seccion>
        )}

        {ayuda.errores && ayuda.errores.length > 0 && (
          <Seccion titulo="Errores comunes">
            <Stack spacing={0.8} sx={{ mt: 0.5 }}>
              {ayuda.errores.map((t) => (
                <Stack key={t} direction="row" spacing={1} alignItems="flex-start">
                  <WarningAmber sx={{ fontSize: 18, color: '#EF4444', mt: '2px' }} />
                  <Typography variant="body2">{t}</Typography>
                </Stack>
              ))}
            </Stack>
          </Seccion>
        )}

        {ayuda.relacionados && ayuda.relacionados.length > 0 && (
          <Seccion titulo="Relacionado">
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 0.5 }}>
              {ayuda.relacionados.map((r) => (
                <Chip key={r.path} label={r.label} icon={<ArrowForward />} variant="outlined" onClick={() => { onClose(); navigate(r.path); }} />
              ))}
            </Stack>
          </Seccion>
        )}
      </Box>

      <Divider />
      <Box sx={{ p: 2 }}>
        <Button fullWidth variant="contained" startIcon={<AutoAwesome />}
          onClick={() => { onClose(); preguntarAlAsistente(ayuda.preguntaAsistente || `Explícame cómo usar ${ayuda.titulo}`); }}>
          Preguntar al asistente
        </Button>
      </Box>
    </Box>
  );
};

/**
 * Botón "?" de la barra superior. Muestra la ayuda del módulo actual y,
 * la primera vez que el usuario entra a un módulo, una invitación a verla.
 */
const BotonAyudaModulo: React.FC = () => {
  const location = useLocation();
  const ayuda = ayudaDeRuta(location.pathname);
  const [abierto, setAbierto] = useState(false);
  const [aviso, setAviso] = useState(false);

  useEffect(() => {
    setAbierto(false);
    if (!ayuda) { setAviso(false); return; }
    const clave = `respondo_ayuda_${ayuda.titulo}`;
    if (!localStorage.getItem(clave)) {
      localStorage.setItem(clave, '1');
      const t = setTimeout(() => setAviso(true), 900);
      return () => clearTimeout(t);
    }
    setAviso(false);
  }, [location.pathname]); // eslint-disable-line

  useEffect(() => {
    const abrir = () => setAbierto(true);
    window.addEventListener('respondo:abrir-ayuda-modulo', abrir);
    return () => window.removeEventListener('respondo:abrir-ayuda-modulo', abrir);
  }, []);

  if (!ayuda) return null;

  return (
    <>
      <Tooltip title={`¿Cómo funciona ${ayuda.titulo}?`}>
        <Button
          onClick={() => { setAviso(false); setAbierto(true); }}
          startIcon={<HelpOutline />}
          size="small"
          sx={{ mr: 1, borderRadius: 5, textTransform: 'none', color: 'primary.dark', bgcolor: '#E8F8EE', '&:hover': { bgcolor: '#D4F2E0' }, minWidth: 0, px: { xs: 1, sm: 1.5 } }}
          aria-label="Ayuda del módulo"
        >
          <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>Ayuda</Box>
        </Button>
      </Tooltip>

      <Drawer anchor="right" open={abierto} onClose={() => setAbierto(false)} sx={{ zIndex: 1400 }}>
        <PanelAyuda ayuda={ayuda} onClose={() => setAbierto(false)} />
      </Drawer>

      <Snackbar open={aviso} onClose={() => setAviso(false)} autoHideDuration={9000} anchorOrigin={{ vertical: 'top', horizontal: 'right' }} sx={{ mt: 7 }}>
        <Alert
          severity="info" icon={<HelpOutline />} onClose={() => setAviso(false)}
          action={<Button color="inherit" size="small" onClick={() => { setAviso(false); setAbierto(true); }}>Ver guía</Button>}
          sx={{ alignItems: 'center', boxShadow: 3 }}
        >
          ¿Primera vez en <b>{ayuda.titulo}</b>? Mira cómo funciona en 1 minuto.
        </Alert>
      </Snackbar>
    </>
  );
};

export default BotonAyudaModulo;
